import { useCallback, useMemo, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { noop } from "lodash";
import useApi from "../../../utils/api";
import { initialState, reducer } from "../../Settings/hook/reducer";
import { ProfileContext } from "../context";
import { useCompany } from "../../../utils/hooks/company";
import { getErrorMessage } from "../../../utils/helpers/errors";

export const useExpectedHoursApi = () => {
	const { t } = useTranslation();
	const { authGet, authPost, authDelete, authPut } = useApi();
	const company = useCompany();
	const { user, isSelf } = useContext(ProfileContext);

	const basePath = useMemo(
		() => (isSelf ? "/profile/expected-hours" : `/users/${user?.id}/expected-hours`),
		[isSelf, user?.id],
	);

	const [state, dispatch] = useReducer(reducer, initialState);

	const selected = useMemo(() => {
		const data = state.data;
		const selectedId = state.selectedId;
		return selectedId !== null && data !== null
			? data.find((resource) => resource.id === selectedId)
			: undefined;
	}, [state.data, state.selectedId]);

	const setMode = useCallback(
		(mode, selectedId = null) =>
			dispatch({
				type: "changeMode",
				payload: { mode, selectedId },
			}),
		[dispatch],
	);

	const goToView = useCallback(() => setMode("view"), [setMode]);
	const goToCreate = useCallback(() => setMode("create"), [setMode]);
	const goToEdit = useCallback((id) => setMode("edit", id), [setMode]);

	const fetch = useCallback(
		async (controller) => {
			dispatch({ type: "requestFetch" });
			try {
				const response = await authGet(basePath, {
					params: { "orderBy[startEffectiveDate]": "desc" },
					signal: controller?.signal,
				});
				if (response) {
					const raw = Array.isArray(response) ? response : response?.result ?? response?.data ?? [];
					const result = [...raw].sort((a, b) => {
						const dA = a?.startEffectiveDate ? new Date(a.startEffectiveDate).getTime() : 0;
						const dB = b?.startEffectiveDate ? new Date(b.startEffectiveDate).getTime() : 0;
						return dB - dA;
					});
					dispatch({
						type: "fetchSucceeded",
						payload: {
							result,
							totalItems: Array.isArray(response) ? response.length : response?.totalItems ?? response?.total ?? 0,
						},
					});
				}
			} catch (err) {
				// Backend not implemented yet: show empty table so UI works without API
				dispatch({
					type: "fetchSucceeded",
					payload: { result: [], totalItems: 0 },
				});
			}
		},
		[authGet, basePath, dispatch],
	);

	const create = useCallback(
		async (data, onSuccess = noop, onFailure = noop) => {
			dispatch({ type: "requestCreate" });
			try {
				const payload = {
					...data,
					company: company?.id,
					status: "active",
					expectedHours: data.expectedHours ?? null,
					startEffectiveDate: data.startEffectiveDate,
					endEffectiveDate: data.endEffectiveDate || null,
					expectedWorkingDays: data.expectedWorkingDays?.length ? data.expectedWorkingDays : null,
					expectedWorkingDaysCount: data.expectedWorkingDaysCount ?? null,
				};
				const response = await authPost(basePath, { data: payload });
				if (response) {
					dispatch({
						type: "createSucceeded",
						payload: response,
					});
					onSuccess(response);
					toast.success(t("resource-created"));
					goToView();
				}
			} catch (err) {
				dispatch({
					type: "createFailed",
					payload: err,
				});
				onFailure(err);
				getErrorMessage(err, t);
			}
		},
		[authPost, basePath, company?.id, t, goToView],
	);

	const update = useCallback(
		async (id, data, onSuccess = noop, onFailure = noop) => {
			dispatch({ type: "requestUpdate" });
			try {
				const payload = {
					...data,
					status: "active",
					startEffectiveDate: data.startEffectiveDate,
					endEffectiveDate: data.endEffectiveDate ?? null,
					expectedHours: data.expectedHours ?? null,
					expectedWorkingDays: data.expectedWorkingDays?.length ? data.expectedWorkingDays : null,
					expectedWorkingDaysCount: data.expectedWorkingDaysCount ?? null,
				};
				const response = await authPut(`${basePath}/${id}`, { data: payload });
				if (response) {
					dispatch({
						type: "updateSucceeded",
						payload: response,
					});
					onSuccess(response);
					toast.success(t("resource-updated"));
					goToView();
				}
			} catch (err) {
				dispatch({ type: "updateFailed", payload: err });
				onFailure(err);
				getErrorMessage(err, t);
			}
		},
		[authPut, basePath, t, goToView],
	);

	const remove = useCallback(
		async (id, onSuccess = noop, onFailure = noop) => {
			const loading = toast(t("deleting-resouce"), {
				autoClose: false,
			});
			try {
				await authDelete(`${basePath}/${id}`);
				dispatch({ type: "delete", payload: id });
				onSuccess(id);
				toast.dismiss(loading);
				toast.success(t("resource-deleted"));
			} catch (err) {
				onFailure(err);
				toast.dismiss(loading);
				getErrorMessage(err, t);
			}
		},
		[authDelete, basePath, t],
	);

	return {
		state,
		fetch,
		create,
		update,
		remove,
		goToCreate,
		goToEdit,
		goToView,
		selected,
	};
};

export const useExpectedHoursByDateRange = (userId) => {
	const { authGet } = useApi();
	const fetchByDateRange = useCallback(
		async (startDate, endDate, signal) => {
			if (!userId || !startDate || !endDate) return null;
			const response = await authGet(`/users/${userId}/expected-hours`, {
				params: { startDate, endDate },
				signal,
			});
			return response;
		},
		[authGet, userId],
	);
	return { fetchByDateRange };
};
